import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFetch } from '../../../hooks/useFetch';
import { GenFilesApiFactory } from '../api/gen-files.api';
import type { ChannelState } from '../types/gen-file.types';

export interface UseCurrentGenFileResult {
  channelState: ChannelState | null;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

export function useCurrentGenFile(): UseCurrentGenFileResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => GenFilesApiFactory(client), [client]);

  const [channelState, setChannelState] = useState<ChannelState | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [revision, setRevision] = useState(0);

  const refresh = useCallback(() => setRevision(r => r + 1), []);

  useEffect(() => {
    const ctrl = new AbortController();
    setError(null);
    api
      .getCurrent({ signal: ctrl.signal })
      .then(data => setChannelState(data.general))
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setChannelState(null);
        setError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => ctrl.abort();
  }, [api, revision]);

  return { channelState, loading, error, refresh };
}
